import { useEffect, useState, useCallback } from 'react';
import { hasVersionUpdate, updateStoredVersion } from '../utils/cacheCleaner';

export interface UpdateState {
  hasUpdate: boolean;
  isChecking: boolean;
  updateSource: 'version' | 'service-worker' | null;
}

/**
 * Hook para gerenciar atualizações da aplicação
 * Funciona tanto com PWA quanto sem PWA
 */
export function useUpdateManager() {
  const [updateState, setUpdateState] = useState<UpdateState>({
    hasUpdate: false,
    isChecking: false,
    updateSource: null,
  });
  const [showUpdateScreen, setShowUpdateScreen] = useState(false);

  /**
   * Verifica atualização por versão do HTML
   */
  const checkVersionUpdate = useCallback((): boolean => {
    const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
    const processedVersion = localStorage.getItem('app-version-processed');
    
    // Se a versão atual já foi processada, não mostrar atualização
    if (currentVersion && processedVersion === currentVersion) {
      // Limpar estado se estava mostrando atualização
      setUpdateState((prev) => {
        if (prev.hasUpdate && prev.updateSource === 'version') {
          return {
            hasUpdate: false,
            isChecking: false,
            updateSource: null,
          };
        }
        return prev;
      });
      return false;
    }
    
    // Verificar se acabamos de atualizar (evitar loop)
    const lastUpdate = localStorage.getItem('app-version-updated');
    if (lastUpdate) {
      const updateTime = parseInt(lastUpdate, 10);
      const now = Date.now();
      // Se atualizou há menos de 10 segundos, não considerar como atualização
      if (now - updateTime < 10000) {
        // Limpar estado se estava mostrando atualização
        setUpdateState((prev) => {
          if (prev.hasUpdate && prev.updateSource === 'version') {
            return {
              hasUpdate: false,
              isChecking: false,
              updateSource: null,
            };
          }
          return prev;
        });
        return false;
      }
    }
    
    const hasUpdate = hasVersionUpdate();
    
    if (hasUpdate) {
      setUpdateState({
        hasUpdate: true,
        isChecking: false,
        updateSource: 'version',
      });
    } else {
      // Se não há atualização, garantir que o estado está limpo
      setUpdateState((prev) => {
        if (prev.hasUpdate && prev.updateSource === 'version') {
          return {
            hasUpdate: false,
            isChecking: false,
            updateSource: null,
          };
        }
        return prev;
      });
    }
    
    return hasUpdate;
  }, []);

  const checkServiceWorkerUpdate = useCallback(async (): Promise<boolean> => {
    return false;
  }, []);

  /**
   * Verifica todas as fontes de atualização
   */
  const checkForUpdates = useCallback(async () => {
    // Não verificar se já está mostrando a tela de atualização
    if (showUpdateScreen) {
      return false;
    }

    setUpdateState((prev) => ({ ...prev, isChecking: true }));

    // Verificar versão do HTML (funciona sem PWA)
    const versionUpdate = checkVersionUpdate();

    // Verificar Service Worker (só funciona com PWA)
    const swUpdate = await checkServiceWorkerUpdate();

    const hasUpdate = versionUpdate || swUpdate;

    setUpdateState((prev) => {
      // Se não há atualização, limpar o estado
      if (!hasUpdate && prev.hasUpdate) {
        return {
          hasUpdate: false,
          isChecking: false,
          updateSource: null,
        };
      }
      // Se há atualização, atualizar o estado
      return {
        ...prev,
        isChecking: false,
        hasUpdate: hasUpdate,
        updateSource: versionUpdate ? 'version' : (swUpdate ? 'service-worker' : prev.updateSource),
      };
    });

    return hasUpdate;
  }, [checkVersionUpdate, checkServiceWorkerUpdate, showUpdateScreen]);

  /**
   * Aplica a atualização mostrando a tela de atualização
   */
  const applyUpdate = useCallback(() => {
    setShowUpdateScreen(true);
  }, []);

  /**
   * Cancela a atualização
   */
  const cancelUpdate = useCallback(() => {
    setUpdateState({
      hasUpdate: false,
      isChecking: false,
      updateSource: null,
    });
    setShowUpdateScreen(false);
  }, []);

  // Verificar atualizações periodicamente
  useEffect(() => {
    // Em desenvolvimento, não verificar atualizações (evita loops com hot reload)
    if (import.meta.env.DEV) {
      return;
    }

    const initialDelay = setTimeout(() => {
      const processedVersion = localStorage.getItem('app-version-processed');
      const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
      
      if (!processedVersion || processedVersion !== currentVersion) {
        checkForUpdates();
      }
    }, 10000);

    const handleFocus = () => {
      const processedVersion = localStorage.getItem('app-version-processed');
      const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
      
      if (!processedVersion || processedVersion !== currentVersion) {
        checkForUpdates();
      }
    };

    window.addEventListener('focus', handleFocus);

    const handleOnline = () => {
      const processedVersion = localStorage.getItem('app-version-processed');
      const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
      
      if (!processedVersion || processedVersion !== currentVersion) {
        checkForUpdates();
      }
    };

    window.addEventListener('online', handleOnline);

    const handleVersionChange = (event: any) => {
      const newVersion = event.detail?.newVersion;
      const processedVersion = localStorage.getItem('app-version-processed');
      
      if (newVersion && processedVersion === newVersion) {
        return;
      }
      
      const hasUpdate = checkVersionUpdate();
      if (hasUpdate) {
        setUpdateState({
          hasUpdate: true,
          isChecking: false,
          updateSource: 'version',
        });
      }
    };

    window.addEventListener('app-version-changed', handleVersionChange);

    return () => {
      clearTimeout(initialDelay);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('app-version-changed', handleVersionChange);
    };
  }, [checkForUpdates, checkVersionUpdate]);

  return {
    updateState,
    showUpdateScreen,
    checkForUpdates,
    applyUpdate,
    cancelUpdate,
    setShowUpdateScreen,
  };
}
